import { useCallback, useEffect, useRef, useState } from 'react'
import type { JSX } from 'react'

import { uuidv7 } from 'uuidv7'

import type { MessageKey } from '@lensmira/i18n'

import {
  ExactError,
  SALE_TYPES,
  SALE_TYPE_DEFINITIONS,
  TAX_TREATMENTS,
  percent,
  type RoundingMode,
  type SaleType,
  type TaxTreatment,
} from '@lensmira/domain'

import { MoneyInputError, formatWireForDisplay, parseMajorInput } from '../money.js'
import type { WireMoney } from '../money.js'
import { toConfirmedOrder, toCreatedOrder, WriteResultMismatch } from '../api/write-results.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useTranslation } from '../i18n.js'
import { connectionLine, writeLine, type Translate } from '../queue/status-line.js'
import { useWriteQueue } from '../queue/use-write-queue.js'
import { FrameChoice, frameName, type FrameOption } from './FrameChoice.js'
import { LensChoice, type LensSelectionData } from './LensChoice.js'
import { saleTypeLabel } from '../status-labels.js'

/**
 * Building a new order. §6.7 `POS-001`-`POS-008a`; §6.39 `SAL-010`, `SAL-011`, `SAL-012`;
 * Appendix C.1, Appendix D, Appendix V.
 *
 * Two writes, each its own idempotency key, because they are two transitions in Appendix C.1's
 * chain, not two views of one save:
 *
 * 1. `POST /orders`, `draft`. Priced, discounted, saved. Nothing is invoiced yet.
 * 2. `POST /orders/confirm`, `draft|quoted -> confirmed`. This is where `OFF-003`'s terminal
 *    block hands out an invoice number, and where `OPS-105` starts requiring a reason to cancel.
 *
 * The task was explicit that collapsing these into one button hides that boundary, so the second
 * step is not reachable until the first one has actually produced an order: the confirm form does
 * not exist on the page until `createdOrder` is showing, and its own hint says in words what the
 * button does before anyone presses it.
 *
 * **`createdOrder` comes off this screen's own write, once the server has answered it.** The
 * confirm step needs the server-assigned `orderId`, which exists nowhere except in the create
 * write's response, and `ConfirmOrder.orderId` is required rather than something this screen can
 * substitute or defer. `queue.ts` records `outcome.body` on a sent write, so the answer is there;
 * {@link useWriteSettled} reads it back by the id this render stored, and
 * `src/api/write-results.ts` maps it rather than casting it, for the reason `src/api/reads.ts`
 * exists at all.
 *
 * That is a correction to what this comment said before, and the correction is the point: it
 * claimed the confirm step was dead because `queue.ts` "never copies `outcome.body`". It does, at
 * `queue.ts:216`. The step was dead because `createdOrder` was a prop `App.tsx` never passed, which
 * is a different file and a different fix, and a browser walkthrough is what separated the two
 * (`PROGRESS.md` problem 111). Both props are kept, so a parent holding the order from a read is
 * still a legitimate source; this screen's own write wins where both exist.
 *
 * **`POS-008a` has no default and this screen does not invent one.** The per-role discount limit
 * is a tenant setting that ships unset; until a shop sets it the rule is inert, and a discount
 * above it is refused for a role holding only `discount_apply_within_limit`. So the discount
 * fields below are plain amount/percent inputs with no max, no slider, and no client-side ceiling:
 * a refusal, if it comes, is the server's, shown in its own words like every other blocked write on
 * this screen.
 *
 * **Every line either prices as a lens or carries a fixed price, never both, never neither**
 * (`orders.service.ts`'s `LINE_PRICE_AMBIGUOUS` / `LINE_PRICE_MISSING`). The pricing-mode radio
 * per line makes that an impossible state to build rather than a validation message to read.
 *
 * **Tax and discount fields have no invented defaults either.** `MR-07` calls a wrong tax rate "a
 * compliance problem, not only an embarrassing one" in the same breath Appendix D refuses to name
 * cash-rounding or discount-limit defaults; the same reasoning keeps tax percent, treatment and
 * code blank until typed, per line, rather than guessing a "standard" Pakistani rate this
 * repository was never given.
 */

const TAX_TREATMENT_KEY: Readonly<Record<TaxTreatment, MessageKey>> = {
  inclusive: 'newOrder.taxTreatment.inclusive',
  exclusive: 'newOrder.taxTreatment.exclusive',
  exempt: 'newOrder.taxTreatment.exempt',
  zero_rated: 'newOrder.taxTreatment.zeroRated',
}

/** `../money.js`'s wire shape, under the name this screen's props use, matching `CashSession.tsx`. */
export type MoneyView = WireMoney

export interface CreatedOrderLineView {
  readonly id: string
  readonly position: number
  readonly description: string
  readonly net: MoneyView
  readonly tax: MoneyView
  readonly gross: MoneyView
  /** `MR-05`: this line's share of the order-level discount. */
  readonly orderDiscountShare: MoneyView
}

/** `orders.service.ts`'s `CreatedOrder`, the fields this screen shows back. */
export interface CreatedOrderView {
  readonly id: string
  readonly subtotal: MoneyView
  readonly tax: MoneyView
  readonly total: MoneyView
  readonly orderDiscount: MoneyView
  readonly lines: readonly CreatedOrderLineView[]
}

/** `orders.service.ts`'s `ConfirmedOrder`, the fields worth showing at the counter. */
export interface ConfirmedOrderView {
  readonly invoiceNumber: string
  readonly sequence: number
}

export interface NewOrderProps {
  /** `BR-05`: every sale is scoped to a branch. */
  readonly branchId: string
  /** `OFF-003`: the invoice number comes from this terminal's own pre-allocated block. */
  readonly terminalId: string
  readonly currency: string
  /** Appendix D names no rounding direction; a country pack supplies it. Never defaulted here. */
  readonly rounding: RoundingMode
  /** `TAX-002`: the series a confirm draws its number from. Tenant/terminal configuration. */
  readonly fiscalYear: number
  readonly seriesCode: string
  readonly seriesTemplate: string
  /** Chosen on an earlier screen (`CustomerSearch`); this screen only carries it forward. */
  readonly customerId?: string
  readonly customerName?: string
  /** Captured on an earlier screen (`RxCapture`); this screen only carries it forward. */
  readonly prescriptionId?: string
  /**
   * `DSS-001`: every makeable lens for the attached prescription, ranked, with its warnings.
   *
   * Absent when there is no prescription to choose against, in which case no lens line can be
   * priced by the engine anyway. A prop rather than a read performed here, because this screen
   * holds no reads config and `WorkQueue`'s `loadQueue` records what an unstable one costs: a new
   * closure each render is an effect that reloads for ever.
   */
  /**
   * The frames this branch can sell, for the chooser. `INV-001`.
   *
   * A prop rather than a read made here, like `loadLensSelection` beside it and for the same
   * reason: this screen holds no reads config, and an unstable closure is an effect that reloads
   * for ever.
   */
  readonly loadFrames?: (query: string) => Promise<readonly FrameOption[]>
  readonly loadLensSelection?: () => Promise<LensSelectionData>
  /** This screen's own `POST /orders` answer, supplied by the parent. See the header comment. */
  readonly createdOrder?: CreatedOrderView
  /** This screen's own `POST /orders/confirm` answer, supplied by the parent. */
  readonly confirmedOrder?: ConfirmedOrderView
  readonly onCreated?: () => void
  readonly onConfirmed?: () => void
}

type PricingMode = 'fixed' | 'lens'
type DiscountMode = 'none' | 'percent' | 'amount'

interface LineState {
  readonly key: string
  /** `SAL-012`: absent means the order's own type. */
  readonly saleType: SaleType | ''
  readonly category: string
  readonly description: string
  readonly quantityText: string
  readonly productId: string
  /**
   * Whether a chooser put the `productId` there, rather than a person typing it.
   *
   * Provenance, not presentation, and it is here because the two cannot be told apart from the
   * value: `FrameChoice` writes a `frame_variant` id and `LensChoice` a `lens_product` id, both
   * UUIDs, into the same field a shopkeeper may type a supplier's code into. The field is "(optional)"
   * precisely so a line with no chooser still has a way to carry one.
   *
   * What it fixes: the id was rendered into an editable text box, so choosing a frame filled a
   * field on screen with `01932c1e-0000-7000-8000-...`. The chooser above it already names the
   * frame, so the box said nothing and invited someone to edit a key.
   */
  readonly productIdChosen: boolean
  /**
   * The exact lot being sold, chosen through `FrameChoice`. `INV-001`.
   *
   * Empty on every line that is not a stock frame. When it is set the server has nothing to
   * resolve: `stock-lines.ts` guesses from a catalogue frame only when the branch holds exactly one
   * candidate, and refuses when it holds several, which at a real shop is most of them.
   */
  readonly stockItemId: string
  readonly pricingMode: PricingMode
  readonly unitPriceText: string
  /**
   * `PRC-007`: one lens, one eye. The powers are **not** here.
   *
   * They come off the prescription server side, which is why this screen no longer asks for them:
   * the dispenser had been retyping sphere, cylinder and add from the prescription attached two
   * sections above, and `AI-001` says what that costs, "a wrong sphere value is a remake".
   */
  readonly eye: 'od' | 'os' | ''
  /** `DSP-003`: which pair, on a two-pair sale. Blank is pair 1. */
  readonly pairNumberText: string
  readonly diameterText: string
  readonly treatmentIdsText: string
  /** `DSP-004`: a progressive has a minimum, which the frame and the fitting decide. */
  readonly fittingHeightText: string
  readonly discountMode: DiscountMode
  readonly discountPercentText: string
  readonly discountAmountText: string
  readonly taxPercentText: string
  readonly taxTreatment: TaxTreatment | ''
  readonly taxCode: string
}

function makeLine(): LineState {
  return {
    key: uuidv7(),
    saleType: '',
    category: '',
    description: '',
    quantityText: '1',
    productId: '',
    productIdChosen: false,
    stockItemId: '',
    pricingMode: 'fixed',
    unitPriceText: '',
    eye: '',
    pairNumberText: '',
    diameterText: '',
    treatmentIdsText: '',
    fittingHeightText: '',
    discountMode: 'none',
    discountPercentText: '',
    discountAmountText: '',
    taxPercentText: '',
    taxTreatment: '',
    taxCode: '',
  }
}

/** Minor units to what a shopkeeper reads, with the currency code in front. `MR-01`. */
function formatMoney(value: MoneyView): string {
  return formatWireForDisplay(value.minor, value.currency)
}

type BuildResult<T> =
  { readonly ok: true; readonly value: T } | { readonly ok: false; readonly error: string }

interface BuiltDiscount {
  readonly percent?: string
  readonly amountMinor?: string
}

/**
 * A discount is a percentage or an amount, never both: `runOrderChain` throws on both being
 * present, at either level, so the radio here makes that the only reachable shape. `percent()` is
 * the domain's own parser, reused purely to validate the `numeric(6,3)` shape a shopkeeper typed;
 * nothing here computes with it.
 */
function buildDiscount(
  t: Translate,
  mode: DiscountMode,
  percentText: string,
  amountText: string,
  currency: string,
  subject: string,
): BuildResult<BuiltDiscount> {
  if (mode === 'none') return { ok: true, value: {} }
  if (mode === 'percent') {
    const trimmed = percentText.trim()
    if (trimmed === '')
      return { ok: false, error: t('newOrder.error.discountPercentMissing', { subject }) }
    try {
      percent(trimmed)
    } catch (error) {
      return {
        ok: false,
        error:
          error instanceof ExactError
            ? t('newOrder.error.discountPrefixed', { subject, message: error.message })
            : t('newOrder.error.discountPercentInvalid', { subject }),
      }
    }
    return { ok: true, value: { percent: trimmed } }
  }
  try {
    return { ok: true, value: { amountMinor: parseMajorInput(amountText, currency).toString() } }
  } catch (error) {
    return {
      ok: false,
      error:
        error instanceof MoneyInputError
          ? t('newOrder.error.discountPrefixed', { subject, message: error.message })
          : t('newOrder.error.discountAmountInvalid', { subject }),
    }
  }
}

/** `orders.service.ts`'s `OrderLineInput`, built here field by field so the wire shape matches. */
interface BuiltOrderLine {
  readonly category: string
  readonly saleType?: SaleType
  readonly description: string
  readonly quantity: number
  readonly productId?: string
  readonly stockItemId?: string
  /**
   * No powers. The server reads them from `prescription_eye` for the eye this line names, which is
   * what `later_todo` 16 settled: there is no field here for a power to disagree with the
   * prescription in.
   */
  /** `DSP-003`: on the line, as `QuoteLineInput` has it, so a frame can say which pair it is too. */
  readonly pairNumber?: number
  readonly lens?: {
    readonly eye: 'od' | 'os'
    readonly diameterMm?: number
    readonly treatmentIds?: readonly string[]
    readonly fittingHeightMm?: number
  }
  readonly unitPriceMinor?: string
  readonly discountPercent?: string
  readonly discountAmountMinor?: string
  readonly taxPercent: string
  readonly taxTreatment: TaxTreatment
  readonly taxCode: string
}

function buildLine(
  t: Translate,
  line: LineState,
  currency: string,
  index: number,
): BuildResult<BuiltOrderLine> {
  const n = index + 1
  const prefixed = (message: string): { readonly ok: false; readonly error: string } => ({
    ok: false,
    error: t('newOrder.error.linePrefixed', { n, message }),
  })
  const category = line.category.trim()
  if (category === '') return { ok: false, error: t('newOrder.error.lineCategoryRequired', { n }) }
  const description = line.description.trim()
  if (description === '')
    return { ok: false, error: t('newOrder.error.lineDescriptionRequired', { n }) }

  const quantity = Number(line.quantityText.trim())
  if (!Number.isInteger(quantity) || quantity < 1) {
    return { ok: false, error: t('newOrder.error.lineQuantityInvalid', { n }) }
  }

  /**
   * `DSP-003`, and outside the lens branch on purpose: a two-pair sale has two frames as well as
   * four lenses, so a frame line has to be able to say which pair it belongs to.
   */
  let pairNumber: number | undefined
  if (line.pairNumberText.trim() !== '') {
    const parsed = Number(line.pairNumberText.trim())
    if (!Number.isInteger(parsed) || parsed < 1) {
      return { ok: false, error: t('newOrder.error.linePairInvalid', { n }) }
    }
    pairNumber = parsed
  }

  let lens: BuiltOrderLine['lens']
  let unitPriceMinor: string | undefined
  if (line.pricingMode === 'lens') {
    /**
     * An eye and nothing else that the prescription already knows.
     *
     * The powers used to be parsed here, through `validateSphere`, `validateCylinder` and
     * `validateAdd`, against values the dispenser typed. The server reads them from
     * `prescription_eye` now, so the only thing this screen has to say is which eye the line is
     * for: a validation that cannot fail has nothing to report, and a field that cannot disagree
     * with the prescription cannot be transcribed wrongly.
     */
    if (line.eye === '') return { ok: false, error: t('newOrder.error.lineEyeRequired', { n }) }

    let diameterMm: number | undefined
    if (line.diameterText.trim() !== '') {
      const parsed = Number(line.diameterText.trim())
      if (!Number.isFinite(parsed) || parsed <= 0) {
        return { ok: false, error: t('newOrder.error.lineDiameterInvalid', { n }) }
      }
      diameterMm = parsed
    }

    let fittingHeightMm: number | undefined
    if (line.fittingHeightText.trim() !== '') {
      const parsed = Number(line.fittingHeightText.trim())
      if (!Number.isFinite(parsed) || parsed <= 0) {
        return { ok: false, error: t('newOrder.error.lineFittingHeightInvalid', { n }) }
      }
      fittingHeightMm = parsed
    }

    const treatmentIds = line.treatmentIdsText
      .split(',')
      .map((id) => id.trim())
      .filter((id) => id !== '')

    lens = {
      eye: line.eye,
      ...(diameterMm === undefined ? {} : { diameterMm }),
      ...(treatmentIds.length === 0 ? {} : { treatmentIds }),
      ...(fittingHeightMm === undefined ? {} : { fittingHeightMm }),
    }
  } else {
    try {
      unitPriceMinor = parseMajorInput(line.unitPriceText, currency).toString()
    } catch (error) {
      return error instanceof MoneyInputError
        ? prefixed(error.message)
        : { ok: false, error: t('newOrder.error.lineUnitPriceInvalid', { n }) }
    }
  }

  const discount = buildDiscount(
    t,
    line.discountMode,
    line.discountPercentText,
    line.discountAmountText,
    currency,
    t('newOrder.discount.lineLegend', { n }),
  )
  if (!discount.ok) return discount

  const taxPercent = line.taxPercentText.trim()
  if (taxPercent === '')
    return { ok: false, error: t('newOrder.error.lineTaxPercentRequired', { n }) }
  try {
    percent(taxPercent)
  } catch (error) {
    return error instanceof ExactError
      ? prefixed(error.message)
      : { ok: false, error: t('newOrder.error.lineTaxPercentInvalid', { n }) }
  }
  if (line.taxTreatment === '')
    return { ok: false, error: t('newOrder.error.lineTaxTreatmentRequired', { n }) }
  const taxTreatment = line.taxTreatment
  const taxCode = line.taxCode.trim()
  if (taxCode === '') return { ok: false, error: t('newOrder.error.lineTaxCodeRequired', { n }) }

  const productId = line.productId.trim()
  const stockItemId = line.stockItemId.trim()

  return {
    ok: true,
    value: {
      category,
      ...(line.saleType === '' ? {} : { saleType: line.saleType }),
      description,
      quantity,
      ...(pairNumber === undefined ? {} : { pairNumber }),
      ...(productId === '' ? {} : { productId }),
      // `INV-001`: the lot, so the sale takes this frame off the shelf rather than none.
      ...(stockItemId === '' ? {} : { stockItemId }),
      ...(lens === undefined ? {} : { lens }),
      ...(unitPriceMinor === undefined ? {} : { unitPriceMinor }),
      ...(discount.value.percent === undefined ? {} : { discountPercent: discount.value.percent }),
      ...(discount.value.amountMinor === undefined
        ? {}
        : { discountAmountMinor: discount.value.amountMinor }),
      taxPercent,
      taxTreatment,
      taxCode,
    },
  }
}

/** `orders.service.ts`'s `CreateOrder`, assembled the same way: fields present only when chosen. */
interface BuiltOrder {
  readonly branchId: string
  readonly saleType: SaleType
  readonly isLayaway?: boolean
  readonly customerId?: string
  readonly prescriptionId?: string
  readonly lines: readonly BuiltOrderLine[]
  readonly orderDiscountPercent?: string
  readonly orderDiscountAmountMinor?: string
  readonly rounding: RoundingMode
}

function buildOrder(
  t: Translate,
  branchId: string,
  saleType: SaleType | '',
  isLayaway: boolean,
  customerId: string | undefined,
  prescriptionId: string | undefined,
  lines: readonly LineState[],
  currency: string,
  orderDiscountMode: DiscountMode,
  orderDiscountPercentText: string,
  orderDiscountAmountText: string,
  rounding: RoundingMode,
): BuildResult<BuiltOrder> {
  if (saleType === '') return { ok: false, error: t('newOrder.error.saleTypeRequired') }
  if (lines.length === 0) return { ok: false, error: t('newOrder.error.noLines') }

  const builtLines: BuiltOrderLine[] = []
  for (const [index, line] of lines.entries()) {
    const built = buildLine(t, line, currency, index)
    if (!built.ok) return built
    builtLines.push(built.value)
  }

  const discount = buildDiscount(
    t,
    orderDiscountMode,
    orderDiscountPercentText,
    orderDiscountAmountText,
    currency,
    t('newOrder.discount.orderLegend'),
  )
  if (!discount.ok) return discount

  return {
    ok: true,
    value: {
      branchId,
      saleType,
      ...(isLayaway ? { isLayaway: true } : {}),
      ...(customerId === undefined ? {} : { customerId }),
      ...(prescriptionId === undefined ? {} : { prescriptionId }),
      lines: builtLines,
      ...(discount.value.percent === undefined
        ? {}
        : { orderDiscountPercent: discount.value.percent }),
      ...(discount.value.amountMinor === undefined
        ? {}
        : { orderDiscountAmountMinor: discount.value.amountMinor }),
      rounding,
    },
  }
}

/**
 * A percent-or-amount-or-none discount, reused for the order and for every line. `POS-008a`: a
 * plain text amount, no max, no slider. See the header comment for why a limit is never shown here.
 */
function DiscountControl({
  idPrefix,
  legend,
  currency,
  mode,
  percentText,
  amountText,
  onModeChange,
  onPercentChange,
  onAmountChange,
}: {
  readonly idPrefix: string
  readonly legend: string
  readonly currency: string
  readonly mode: DiscountMode
  readonly percentText: string
  readonly amountText: string
  readonly onModeChange: (mode: DiscountMode) => void
  readonly onPercentChange: (text: string) => void
  readonly onAmountChange: (text: string) => void
}): JSX.Element {
  const { t } = useTranslation()
  return (
    <fieldset className="lm-field">
      <legend className="lm-field__label">{legend}</legend>
      <label>
        <input
          type="radio"
          name={`${idPrefix}-mode`}
          value="none"
          checked={mode === 'none'}
          onChange={() => onModeChange('none')}
        />{' '}
        {t('newOrder.discount.noDiscount', { legend })}
      </label>
      <label>
        <input
          type="radio"
          name={`${idPrefix}-mode`}
          value="percent"
          checked={mode === 'percent'}
          onChange={() => onModeChange('percent')}
        />{' '}
        {t('newOrder.discount.percent', { legend })}
      </label>
      <label>
        <input
          type="radio"
          name={`${idPrefix}-mode`}
          value="amount"
          checked={mode === 'amount'}
          onChange={() => onModeChange('amount')}
        />{' '}
        {t('newOrder.discount.amount', { legend })}
      </label>

      {mode === 'percent' ? (
        <label className="lm-field lm-field--inline">
          <span className="lm-field__label">{t('newOrder.discount.percentField', { legend })}</span>
          <input
            className="lm-input"
            inputMode="decimal"
            autoComplete="off"
            value={percentText}
            onChange={(e) => onPercentChange(e.currentTarget.value)}
          />
        </label>
      ) : null}
      {mode === 'amount' ? (
        <label className="lm-field lm-field--inline">
          <span className="lm-field__label">
            {t('newOrder.discount.amountField', { legend, currency })}
          </span>
          <input
            className="lm-input"
            inputMode="decimal"
            autoComplete="off"
            value={amountText}
            onChange={(e) => onAmountChange(e.currentTarget.value)}
          />
        </label>
      ) : null}
    </fieldset>
  )
}

/** One order line, editable. `SAL-012`'s per-line sale type, the fixed-or-lens price, its own
 *  discount and its own tax: the four things `OrderLineInput` needs from a line. */
/**
 * The fixed-price lines on this order other than `exceptKey`, summed in minor units. `DSP-006`.
 *
 * **Lens-priced lines are left out and the comparison says so**, because their price is whatever
 * the engine resolves for the lens eventually chosen: including a guess here would make the total
 * move when an unrelated line is picked. A line whose price has not been typed yet, or is typed
 * wrong, contributes nothing rather than throwing: this feeds a comparison, not an invoice, and a
 * half-filled form must not take the lens chooser down with it.
 *
 * `parseMajorInput` and never `Number`, which is the same rule the order build itself follows: a
 * price goes from the box to `bigint` minor units with no float in between (Appendix D).
 */
function otherLinesOf(
  lines: readonly LineState[],
  exceptKey: string,
  currency: string,
): bigint {
  let total = 0n
  for (const line of lines) {
    if (line.key === exceptKey) continue
    if (line.pricingMode !== 'fixed') continue
    const quantity = Number.parseInt(line.quantityText.trim(), 10)
    if (!Number.isSafeInteger(quantity) || quantity < 1) continue
    try {
      total += parseMajorInput(line.unitPriceText, currency) * BigInt(quantity)
    } catch {
      continue
    }
  }
  return total
}

function LineEditor({
  line,
  index,
  currency,
  canRemove,
  loadFrames,
  loadLensSelection,
  otherLinesMinor,
  onChange,
  onRemove,
}: {
  readonly line: LineState
  readonly index: number
  readonly currency: string
  readonly canRemove: boolean
  /** `INV-001`, threaded from the screen. Absent when the shell has no reads config. */
  readonly loadFrames?: (query: string) => Promise<readonly FrameOption[]>
  /** `DSS-001`, threaded from the screen. Absent when no prescription is attached. */
  readonly loadLensSelection?: () => Promise<LensSelectionData>
  /**
   * `DSP-006`: the fixed-price lines already on this order, except this one, in minor units.
   *
   * Summed by the screen and passed in, because a line does not know about its siblings and the
   * comparison's total is about the whole order rather than this line.
   */
  readonly otherLinesMinor: bigint
  readonly onChange: (patch: Partial<LineState>) => void
  readonly onRemove: () => void
}): JSX.Element {
  const { t } = useTranslation()
  const n = index + 1

  return (
    <div className="lm-order-line">
      <div className="lm-screen__actions">
        <span className="lm-field__label">{t('newOrder.line.label', { n })}</span>
        {canRemove ? (
          <button type="button" className="lm-button lm-button--secondary" onClick={onRemove}>
            {t('newOrder.line.remove', { n })}
          </button>
        ) : null}
      </div>

      <label className="lm-field">
        <span className="lm-field__label">{t('newOrder.line.saleType', { n })}</span>
        <select
          className="lm-input"
          value={line.saleType}
          onChange={(e) => onChange({ saleType: e.currentTarget.value as SaleType | '' })}
        >
          <option value="">{t('newOrder.line.saleTypeSameAsOrder')}</option>
          {SALE_TYPES.map((type) => (
            <option key={type} value={type}>
              {saleTypeLabel(t, type)}
            </option>
          ))}
        </select>
      </label>

      <label className="lm-field">
        <span className="lm-field__label">{t('newOrder.line.category', { n })}</span>
        <input
          className="lm-input"
          autoComplete="off"
          value={line.category}
          onChange={(e) => onChange({ category: e.currentTarget.value })}
        />
      </label>

      <label className="lm-field">
        <span className="lm-field__label">{t('newOrder.line.description', { n })}</span>
        <input
          className="lm-input"
          autoComplete="off"
          value={line.description}
          onChange={(e) => onChange({ description: e.currentTarget.value })}
        />
      </label>

      <label className="lm-field lm-field--inline">
        <span className="lm-field__label">{t('newOrder.line.quantity', { n })}</span>
        <input
          className="lm-input"
          type="number"
          min={1}
          step={1}
          value={line.quantityText}
          onChange={(e) => onChange({ quantityText: e.currentTarget.value })}
        />
      </label>

      <label className="lm-field lm-field--inline">
        <span className="lm-field__label">{t('newOrder.line.pairNumber', { n })}</span>
        {/* Every line, not only a lens: a two-pair sale has two frames, and a frame that cannot
            say which pair it is leaves the job ticket guessing. `DSP-003`. */}
        <input
          className="lm-input"
          inputMode="numeric"
          autoComplete="off"
          value={line.pairNumberText}
          onChange={(e) => onChange({ pairNumberText: e.currentTarget.value })}
        />
      </label>
      {loadFrames === undefined ? null : (
        <FrameChoice
          load={loadFrames}
          {...(line.stockItemId.trim() === ''
            ? {}
            : { chosenStockItemId: line.stockItemId.trim() })}
          onChoose={(option) => {
            // The lot, the catalogue frame and a description, so the dispenser types none of them.
            // The description is filled only when empty: an edited one is theirs.
            onChange({
              stockItemId: option.stockItemId,
              productId: option.frameVariantId,
              productIdChosen: true,
              ...(line.description.trim() === '' ? { description: frameName(option) } : {}),
            })
          }}
        />
      )}
      {line.productIdChosen ? null : (
        <label className="lm-field">
          <span className="lm-field__label">{t('newOrder.line.productId', { n })}</span>
          <input
            className="lm-input"
            autoComplete="off"
            value={line.productId}
            onChange={(e) => onChange({ productId: e.currentTarget.value })}
          />
        </label>
      )}

      <fieldset className="lm-field">
        <legend className="lm-field__label">{t('newOrder.line.priceLegend', { n })}</legend>
        <label>
          <input
            type="radio"
            name={`pricing-mode-${line.key}`}
            value="fixed"
            checked={line.pricingMode === 'fixed'}
            onChange={() => onChange({ pricingMode: 'fixed' })}
          />{' '}
          {t('newOrder.line.priceFixed', { n })}
        </label>
        <label>
          <input
            type="radio"
            name={`pricing-mode-${line.key}`}
            value="lens"
            checked={line.pricingMode === 'lens'}
            onChange={() => onChange({ pricingMode: 'lens' })}
          />{' '}
          {t('newOrder.line.priceLens', { n })}
        </label>
      </fieldset>

      {line.pricingMode === 'fixed' ? (
        <label className="lm-field">
          <span className="lm-field__label">{t('newOrder.line.unitPrice', { n, currency })}</span>
          <input
            className="lm-input"
            inputMode="decimal"
            autoComplete="off"
            value={line.unitPriceText}
            onChange={(e) => onChange({ unitPriceText: e.currentTarget.value })}
          />
        </label>
      ) : (
        <>
          {loadLensSelection === undefined ? (
            <p className="lm-hint">
              {/* No prescription attached, so there is nothing to choose a lens against and the
                  engine has no powers to price one by. Said here rather than leaving the chooser
                  out silently. */}
              {t('newOrder.line.noPrescriptionForLens')}
            </p>
          ) : (
            <LensChoice
              load={loadLensSelection}
              otherLinesMinor={otherLinesMinor}
              {...(line.productId.trim() === '' ? {} : { chosenProductId: line.productId.trim() })}
              onChoose={(choice) => {
                // The product id and a description, so the dispenser types neither. The
                // description is only filled when empty: an edited one is theirs.
                onChange({
                  productId: choice.productId,
                  productIdChosen: true,
                  ...(line.description.trim() === '' ? { description: choice.name } : {}),
                })
              }}
            />
          )}
          <label className="lm-field lm-field--inline">
            <span className="lm-field__label">{t('newOrder.line.eye', { n })}</span>
            <select
              className="lm-input"
              value={line.eye}
              onChange={(e) => onChange({ eye: e.currentTarget.value as LineState['eye'] })}
            >
              {/* No default. `PRC-007` prices one lens and the server reads that eye's powers off
                  the prescription, so a wrong pre-selection would order the wrong lens silently. */}
              <option value="">{t('newOrder.line.eyeUnset')}</option>
              <option value="od">{t('newOrder.line.eyeRight')}</option>
              <option value="os">{t('newOrder.line.eyeLeft')}</option>
            </select>
          </label>
          <label className="lm-field lm-field--inline">
            <span className="lm-field__label">{t('newOrder.line.diameter', { n })}</span>
            <input
              className="lm-input"
              inputMode="decimal"
              autoComplete="off"
              value={line.diameterText}
              onChange={(e) => onChange({ diameterText: e.currentTarget.value })}
            />
          </label>
          <label className="lm-field lm-field--inline">
            <span className="lm-field__label">{t('newOrder.line.fittingHeight', { n })}</span>
            <input
              className="lm-input"
              inputMode="decimal"
              autoComplete="off"
              value={line.fittingHeightText}
              onChange={(e) => onChange({ fittingHeightText: e.currentTarget.value })}
            />
          </label>
          <label className="lm-field">
            <span className="lm-field__label">{t('newOrder.line.treatments', { n })}</span>
            <input
              className="lm-input"
              autoComplete="off"
              value={line.treatmentIdsText}
              onChange={(e) => onChange({ treatmentIdsText: e.currentTarget.value })}
            />
          </label>
        </>
      )}

      <DiscountControl
        idPrefix={`line-discount-${line.key}`}
        legend={t('newOrder.discount.lineLegend', { n })}
        currency={currency}
        mode={line.discountMode}
        percentText={line.discountPercentText}
        amountText={line.discountAmountText}
        onModeChange={(discountMode) => onChange({ discountMode })}
        onPercentChange={(discountPercentText) => onChange({ discountPercentText })}
        onAmountChange={(discountAmountText) => onChange({ discountAmountText })}
      />

      <label className="lm-field lm-field--inline">
        <span className="lm-field__label">{t('newOrder.line.taxPercent', { n })}</span>
        <input
          className="lm-input"
          inputMode="decimal"
          autoComplete="off"
          value={line.taxPercentText}
          onChange={(e) => onChange({ taxPercentText: e.currentTarget.value })}
        />
      </label>
      <label className="lm-field">
        <span className="lm-field__label">{t('newOrder.line.taxTreatment', { n })}</span>
        <select
          className="lm-input"
          value={line.taxTreatment}
          onChange={(e) => onChange({ taxTreatment: e.currentTarget.value as TaxTreatment | '' })}
        >
          <option value="">{t('newOrder.taxTreatment.choose')}</option>
          {TAX_TREATMENTS.map((treatment) => (
            <option key={treatment} value={treatment}>
              {t(TAX_TREATMENT_KEY[treatment])}
            </option>
          ))}
        </select>
      </label>
      <label className="lm-field">
        <span className="lm-field__label">{t('newOrder.line.taxCode', { n })}</span>
        <input
          className="lm-input"
          autoComplete="off"
          value={line.taxCode}
          onChange={(e) => onChange({ taxCode: e.currentTarget.value })}
        />
      </label>
    </div>
  )
}

export function NewOrder({
  branchId,
  terminalId,
  currency,
  rounding,
  fiscalYear,
  seriesCode,
  seriesTemplate,
  customerId,
  customerName,
  prescriptionId,
  loadFrames,
  loadLensSelection,
  createdOrder,
  confirmedOrder,
  onCreated,
  onConfirmed,
}: NewOrderProps): JSX.Element {
  const [saleType, setSaleType] = useState<SaleType | ''>('')
  const [isLayaway, setIsLayaway] = useState(false)
  const [lines, setLines] = useState<readonly LineState[]>(() => [makeLine()])
  const [orderDiscountMode, setOrderDiscountMode] = useState<DiscountMode>('none')
  const [orderDiscountPercentText, setOrderDiscountPercentText] = useState('')
  const [orderDiscountAmountText, setOrderDiscountAmountText] = useState('')

  const [createWriteId, setCreateWriteId] = useState<string | null>(null)
  const [orderRevealed, setOrderRevealed] = useState(false)
  /**
   * `DSP-001`: "Quotes carry an expiry date". Required, with no default, because the PRD names no
   * period and the domain refuses an absent one as `QUOTE_EXPIRY_REQUIRED`. A date this screen
   * invented would be a promise about a price that nobody made.
   */
  const [quoteExpiresText, setQuoteExpiresText] = useState('')
  const [quoteWriteId, setQuoteWriteId] = useState<string | null>(null)
  const [quoteError, setQuoteError] = useState<string | null>(null)
  const [confirmWriteId, setConfirmWriteId] = useState<string | null>(null)
  const [confirmRevealed, setConfirmRevealed] = useState(false)

  const { enqueue, snapshot, discard } = useWriteQueue()
  const { t } = useTranslation()
  const saleTypeField = useRef<HTMLSelectElement>(null)
  // ARC-006, the same synchronous guard ReturnLine.tsx uses: set true before the first `await`,
  // so a second click arriving in the same tick (before React commits the disabled attribute)
  // still sees it and returns rather than enqueueing twice.
  const creating = useRef(false)
  const confirming = useRef(false)

  useEffect(() => {
    // KB-02: SAL-010's "first choice in a sale" is focused on open, no click to start.
    saleTypeField.current?.focus()
  }, [])

  const updateLine = useCallback((key: string, patch: Partial<LineState>) => {
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)))
  }, [])

  const addLine = useCallback(() => {
    setLines((prev) => [...prev, makeLine()])
  }, [])

  const removeLine = useCallback((key: string) => {
    // Never below one line: an order with none is ORDER_EMPTY, not a smaller order.
    setLines((prev) => (prev.length <= 1 ? prev : prev.filter((l) => l.key !== key)))
  }, [])

  const built = buildOrder(
    t,
    branchId,
    saleType,
    isLayaway,
    customerId,
    prescriptionId,
    lines,
    currency,
    orderDiscountMode,
    orderDiscountPercentText,
    orderDiscountAmountText,
    rounding,
  )

  const currentCreateWrite = createWriteId
    ? snapshot.writes.find((w) => w.id === createWriteId)
    : undefined
  const currentConfirmWrite = confirmWriteId
    ? snapshot.writes.find((w) => w.id === confirmWriteId)
    : undefined

  /**
   * The two answers, and the parent told when the **server** has them rather than when the queue
   * does. `onCreated` re-reads the orders list; fired at enqueue time it read the list before the
   * POST had been sent and the new order was not in it.
   */
  const [createdHere, setCreatedHere] = useState<CreatedOrderView | undefined>(undefined)
  const [confirmedHere, setConfirmedHere] = useState<ConfirmedOrderView | undefined>(undefined)
  const [resultError, setResultError] = useState<string | null>(null)

  useWriteSettled(snapshot, createWriteId, (write) => {
    try {
      setCreatedHere(toCreatedOrder(write.response))
    } catch (error) {
      // The draft exists; it is the answer that could not be read. Said plainly, because the next
      // step is unreachable without the id and a silent absence looks like a screen that hung.
      setResultError(
        error instanceof WriteResultMismatch ? error.message : t('newOrder.error.createdReadBack'),
      )
    }
    onCreated?.()
  })

  useWriteSettled(snapshot, confirmWriteId, (write) => {
    try {
      setConfirmedHere(toConfirmedOrder(write.response))
    } catch (error) {
      setResultError(
        error instanceof WriteResultMismatch
          ? error.message
          : t('newOrder.error.confirmedReadBack'),
      )
    }
    onConfirmed?.()
  })

  // This screen's own answers where it has them, the parent's otherwise.
  const created = createdHere ?? createdOrder
  const confirmed = confirmedHere ?? confirmedOrder

  /**
   * `DSP-001`: issue a quote from the same lines. `draft -> quoted`.
   *
   * Refused here, before the queue, for the two things the body cannot carry. A quote with no
   * customer has nobody to come back with it, and `IssueQuote.customerId` is required; a quote with
   * a discount is refused by `parseLines`'s own `refuseKeys`, so filling one in and losing it
   * silently is the outcome this prevents. Both messages name the rule rather than the field.
   */
  const issueQuote = useCallback(async () => {
    if (!built.ok) return
    if (customerId === undefined) {
      setQuoteError(t('newOrder.quote.customerRequired'))
      return
    }
    if (quoteExpiresText.trim() === '') {
      setQuoteError(t('newOrder.quote.expiryRequired'))
      return
    }
    const discounted =
      built.value.orderDiscountPercent !== undefined ||
      built.value.orderDiscountAmountMinor !== undefined ||
      built.value.lines.some(
        (line) => line.discountPercent !== undefined || line.discountAmountMinor !== undefined,
      )
    if (discounted) {
      setQuoteError(t('newOrder.quote.noDiscounts'))
      return
    }
    setQuoteError(null)

    const write = (await enqueue({
      method: 'POST',
      path: '/quotes',
      label: customerName
        ? t('newOrder.quote.queueLabel', { customerName })
        : t('newOrder.quote.heading'),
      body: {
        branchId,
        customerId,
        ...(prescriptionId === undefined ? {} : { prescriptionId }),
        saleType: built.value.saleType,
        rounding: built.value.rounding,
        // A calendar date from the field, as an instant at the end of that day in UTC. The domain
        // compares it against the issue date and refuses one that is not after it.
        expiresAt: `${quoteExpiresText.trim()}T23:59:59.000Z`,
        // The same lines, minus what a quote does not carry. `refuseKeys` would refuse the
        // discount fields and `orderDate`, `isLayaway` and `terminalId` are an order's.
        lines: built.value.lines.map((line) => ({
          category: line.category,
          ...(line.saleType === undefined ? {} : { saleType: line.saleType }),
          description: line.description,
          quantity: line.quantity,
          ...(line.pairNumber === undefined ? {} : { pairNumber: line.pairNumber }),
          ...(line.productId === undefined ? {} : { productId: line.productId }),
          ...(line.lens === undefined ? {} : { lens: line.lens }),
          ...(line.unitPriceMinor === undefined ? {} : { unitPriceMinor: line.unitPriceMinor }),
          taxPercent: line.taxPercent,
          taxTreatment: line.taxTreatment,
          taxCode: line.taxCode,
        })),
      },
    })) as { id: string }
    setQuoteWriteId(write.id)
  }, [built, branchId, customerId, customerName, prescriptionId, quoteExpiresText, enqueue, t])

  const createOrder = useCallback(async () => {
    if (creating.current || !built.ok) return
    creating.current = true
    try {
      // A blocked create is discarded before a corrected one is queued, same as ReturnLine: the
      // queue drains strictly in order, so a still-blocked write ahead of a new one would hold it
      // forever.
      if (currentCreateWrite?.status === 'blocked') await discard(currentCreateWrite.id)
      // Set before the await, as CashSession's blind count does: this render is the one that
      // attempted the create, regardless of how long the queue takes with it.
      setOrderRevealed(true)
      const write = (await enqueue({
        method: 'POST',
        path: '/orders',
        label: customerName ? t('newOrder.queue.createLabel', { customerName }) : t('nav.newOrder'),
        body: built.value,
      })) as { id: string }
      setCreateWriteId(write.id)
    } finally {
      creating.current = false
    }
  }, [built, currentCreateWrite, customerName, discard, enqueue])

  const confirmOrder = useCallback(async () => {
    if (confirming.current || !created) return
    confirming.current = true
    try {
      if (currentConfirmWrite?.status === 'blocked') await discard(currentConfirmWrite.id)
      setConfirmRevealed(true)
      const write = (await enqueue({
        method: 'POST',
        path: '/orders/confirm',
        label: t('newOrder.queue.confirmLabel', { orderId: created.id }),
        body: {
          orderId: created.id,
          terminalId,
          fiscalYear,
          seriesCode,
          seriesTemplate,
        },
      })) as { id: string }
      setConfirmWriteId(write.id)
    } finally {
      confirming.current = false
    }
  }, [
    created,
    currentConfirmWrite,
    discard,
    enqueue,
    fiscalYear,
    seriesCode,
    seriesTemplate,
    terminalId,
  ])

  const orderCreated = orderRevealed && created !== undefined
  const orderConfirmed = confirmRevealed && confirmed !== undefined

  const createStatus = writeLine(t, snapshot, createWriteId, t('queue.subject.order'))
  const confirmStatus = writeLine(t, snapshot, confirmWriteId, t('queue.subject.confirmation'))
  const quoteStatus = writeLine(t, snapshot, quoteWriteId, t('queue.subject.quote'))

  return (
    <section className="lm-screen" aria-labelledby="new-order-heading">
      <h1 id="new-order-heading" className="lm-screen__heading">
        {t('nav.newOrder')}
      </h1>

      <p className="lm-queue-state" role="status">
        {connectionLine(t, snapshot)}
      </p>

      <p className="lm-hint">
        {customerName ? t('newOrder.customerLine', { customerName }) : t('newOrder.noCustomer')}{' '}
        {prescriptionId ? t('newOrder.prescriptionAttached') : t('newOrder.noPrescription')}
      </p>

      {!orderCreated ? (
        <form
          className="lm-form"
          onSubmit={(e) => {
            e.preventDefault()
            void createOrder()
          }}
        >
          <label className="lm-field">
            <span className="lm-field__label">{t('newOrder.saleType.label')}</span>
            <select
              ref={saleTypeField}
              className="lm-input"
              value={saleType}
              onChange={(e) => setSaleType(e.currentTarget.value as SaleType | '')}
            >
              <option value="">{t('newOrder.saleType.choose')}</option>
              {SALE_TYPES.map((type) => (
                <option key={type} value={type}>
                  {saleTypeLabel(t, type)}
                </option>
              ))}
            </select>
          </label>

          {saleType !== '' ? (
            <p className="lm-hint">
              {SALE_TYPE_DEFINITIONS[saleType].prescription === 'none'
                ? t('newOrder.prescription.notNeeded', { saleType: saleTypeLabel(t, saleType) })
                : t(
                    prescriptionId
                      ? 'newOrder.prescription.needed'
                      : 'newOrder.prescription.neededNoneAttached',
                    {
                      saleType: saleTypeLabel(t, saleType),
                      kind: t(
                        SALE_TYPE_DEFINITIONS[saleType].prescription === 'contact_lens'
                          ? 'newOrder.prescriptionKind.contactLens'
                          : 'newOrder.prescriptionKind.spectacle',
                      ),
                    },
                  )}
            </p>
          ) : null}

          <label className="lm-field lm-field--inline">
            <input
              type="checkbox"
              checked={isLayaway}
              onChange={(e) => setIsLayaway(e.currentTarget.checked)}
            />
            <span className="lm-field__label">{t('newOrder.layaway')}</span>
          </label>

          <h2 className="lm-screen__subheading">{t('common.lines')}</h2>

          <div className="lm-order-lines">
            {lines.map((line, index) => (
              <LineEditor
                key={line.key}
                line={line}
                index={index}
                currency={currency}
                canRemove={lines.length > 1}
                otherLinesMinor={otherLinesOf(lines, line.key, currency)}
                {...(loadFrames === undefined ? {} : { loadFrames })}
                {...(loadLensSelection === undefined ? {} : { loadLensSelection })}
                onChange={(patch) => updateLine(line.key, patch)}
                onRemove={() => removeLine(line.key)}
              />
            ))}
          </div>

          <div className="lm-screen__actions">
            <button type="button" className="lm-button lm-button--secondary" onClick={addLine}>
              {t('newOrder.lines.addAnother')}
            </button>
          </div>

          <DiscountControl
            idPrefix="order-discount"
            legend={t('newOrder.discount.orderLegend')}
            currency={currency}
            mode={orderDiscountMode}
            percentText={orderDiscountPercentText}
            amountText={orderDiscountAmountText}
            onModeChange={setOrderDiscountMode}
            onPercentChange={setOrderDiscountPercentText}
            onAmountChange={setOrderDiscountAmountText}
          />
          <p className="lm-hint">{t('newOrder.discount.limitNote')}</p>

          {!built.ok ? (
            <p className="lm-hint" role="status">
              {built.error}
            </p>
          ) : null}

          <div className="lm-screen__actions">
            <button
              type="submit"
              className="lm-button lm-button--primary"
              disabled={
                !built.ok ||
                (currentCreateWrite !== undefined && currentCreateWrite.status !== 'blocked')
              }
            >
              {t('newOrder.createButton')}
            </button>
            <p className="lm-hint">{t('newOrder.createHint')}</p>
          </div>

          {/* `DSP-001`: the same sale, quoted instead of sold. Its own fieldset rather than a mode
              switch, because the lines above are identical either way and a mode would make a
              dispenser re-enter them to change their mind. */}
          <fieldset className="lm-fieldset">
            <legend className="lm-field__label">{t('newOrder.quote.heading')}</legend>
            <label className="lm-field lm-field--inline">
              <span className="lm-field__label">{t('newOrder.quote.expiresLabel')}</span>
              <input
                className="lm-input"
                type="date"
                value={quoteExpiresText}
                onChange={(e) => setQuoteExpiresText(e.currentTarget.value)}
              />
            </label>
            <p className="lm-hint">{t('newOrder.quote.hint')}</p>
            {quoteError === null ? null : (
              <p className="lm-hint" role="alert">
                {quoteError}
              </p>
            )}
            <button
              type="button"
              className="lm-button lm-button--secondary"
              disabled={!built.ok}
              onClick={() => void issueQuote()}
            >
              {t('newOrder.quote.issueButton')}
            </button>
            {quoteStatus === null ? null : (
              <p className="lm-queue-state" role="status">
                {quoteStatus}
              </p>
            )}
          </fieldset>

          {createStatus ? (
            <p className="lm-queue-state" role="status">
              {createStatus}
            </p>
          ) : null}
        </form>
      ) : (
        <>
          {resultError ? (
            <p className="lm-hint" role="status">
              {resultError}
            </p>
          ) : null}
          {created ? (
            <div className="lm-paste-report" role="status">
              <h2 className="lm-screen__subheading">{t('newOrder.created.heading')}</h2>
              <p className="lm-paste-report__line">
                {t('newOrder.created.totals', {
                  subtotal: formatMoney(created.subtotal),
                  tax: formatMoney(created.tax),
                  total: formatMoney(created.total),
                })}
              </p>
              {BigInt(created.orderDiscount.minor) !== 0n ? (
                <p className="lm-paste-report__line">
                  {t('newOrder.created.orderDiscount', {
                    discount: formatMoney(created.orderDiscount),
                  })}
                </p>
              ) : null}
              {created.lines.map((l) => (
                <p key={l.id} className="lm-paste-report__line">
                  {t('newOrder.created.lineReport', {
                    position: l.position,
                    description: l.description,
                    gross: formatMoney(l.gross),
                    net: formatMoney(l.net),
                    tax: formatMoney(l.tax),
                  })}
                </p>
              ))}
            </div>
          ) : null}

          {!orderConfirmed ? (
            <form
              className="lm-form"
              onSubmit={(e) => {
                e.preventDefault()
                void confirmOrder()
              }}
            >
              <h2 className="lm-screen__subheading">{t('newOrder.confirm.heading')}</h2>
              <p className="lm-hint">{t('newOrder.confirm.hint', { seriesCode, fiscalYear })}</p>

              <div className="lm-screen__actions">
                <button
                  type="submit"
                  className="lm-button lm-button--primary"
                  disabled={
                    currentConfirmWrite !== undefined && currentConfirmWrite.status !== 'blocked'
                  }
                >
                  {t('newOrder.confirm.button')}
                </button>
              </div>

              {confirmStatus ? (
                <p className="lm-queue-state" role="status">
                  {confirmStatus}
                </p>
              ) : null}
            </form>
          ) : confirmed ? (
            <div className="lm-paste-report" role="status">
              <h2 className="lm-screen__subheading">{t('newOrder.confirmed.heading')}</h2>
              <p className="lm-paste-report__line">
                {t('newOrder.confirmed.invoice', {
                  invoiceNumber: confirmed.invoiceNumber,
                  sequence: confirmed.sequence,
                })}
              </p>
            </div>
          ) : null}
        </>
      )}
    </section>
  )
}
